import React, { useState, useRef, useEffect } from 'react';
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { createPortal } from 'react-dom';
import { useClickOutside } from 'services/useClickOutside';
import { formatDate, formatDateLocal } from './formatConventions';

// ##### Color schemes — only selection/interaction colors change per module
// Green (saved/confirmed) is always kept semantic and consistent across schemes
const COLOR_SCHEMES = {
  blue: {
    focusRing: 'focus:ring-blue-700',
    selectedBg: 'bg-blue-700 text-white',
    rangeBg: 'bg-blue-100',
    todayRing: 'ring-blue-500',
    todayBtn: 'text-blue-700 hover:bg-blue-50',
    applyBtn: 'bg-blue-700 hover:bg-blue-800',
  },
  gray: {
    focusRing: 'focus:ring-gray-600',
    selectedBg: 'bg-gray-700 text-white',
    rangeBg: 'bg-gray-200',
    todayRing: 'ring-gray-500',
    todayBtn: 'text-gray-700 hover:bg-gray-50',
    applyBtn: 'bg-gray-700 hover:bg-gray-800',
  },
  green: {
    focusRing: 'focus:ring-green-700',
    selectedBg: 'bg-green-700 text-white',
    rangeBg: 'bg-green-100',
    todayRing: 'ring-green-600',
    todayBtn: 'text-green-800 hover:bg-green-50',
    applyBtn: 'bg-green-700 hover:bg-green-800',
  },
};

// ##### DatePicker Component - Reusable date picker with calendar dropdown
const DatePicker = ({
  value = '', // For single mode: "YYYY-MM-DD", for range mode: { start: "YYYY-MM-DD", end: "YYYY-MM-DD" }
  onChange,
  name = 'date',
  mode = 'single', // 'single' | 'range'
  colorScheme = 'blue', // 'blue' | 'gray' | 'green'
  disabled = false,
  required = false,
  className = '',
  placeholder = 'Select date',
  /** Z-index for the calendar portal (e.g. 61 when the trigger sits inside a z-60 modal). */
  dropdownZIndex = 50,
}) => {
  const colors = COLOR_SCHEMES[colorScheme] || COLOR_SCHEMES.blue;

  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [pendingDate, setPendingDate] = useState(null); // Temporary selected date (single mode)
  const [pendingStartDate, setPendingStartDate] = useState(null); // Temporary start date (range mode)
  const [pendingEndDate, setPendingEndDate] = useState(null); // Temporary end date (range mode)
  const [selectionMode, setSelectionMode] = useState('start'); // 'start' | 'end' (range mode only)
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  // Close on Escape before parent dialogs (nested sheets / modals) handle bubbling Esc
  useEffect(() => {
    if (!isOpen) return undefined;
    const onEsc = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setIsOpen(false);
    };
    document.addEventListener('keydown', onEsc, true);
    return () => document.removeEventListener('keydown', onEsc, true);
  }, [isOpen]);

  // ##### Handle click outside to close dropdown
  useClickOutside([buttonRef, dropdownRef], () => setIsOpen(false), isOpen);

  // ##### Initialize current month from value if provided, or when opening dropdown
  useEffect(() => {
    if (isOpen) {
      if (mode === 'range') {
        if (value && typeof value === 'object' && value.start) {
          const [year, month, day] = value.start.split('-').map(Number);
          const date = new Date(year, month - 1, day);
          if (!isNaN(date.getTime())) {
            setCurrentMonth(date);
            setPendingStartDate(null);
            setPendingEndDate(null);
            setSelectionMode('start');
          } else {
            setPendingStartDate(null);
            setPendingEndDate(null);
            setSelectionMode('start');
          }
        } else {
          setPendingStartDate(null);
          setPendingEndDate(null);
          setSelectionMode('start');
        }
      } else {
        if (value) {
          const [year, month, day] = value.split('-').map(Number);
          const date = new Date(year, month - 1, day);
          if (!isNaN(date.getTime())) {
            setCurrentMonth(date);
            setPendingDate(null);
          } else {
            setPendingDate(null);
          }
        } else {
          setPendingDate(null);
        }
      }
    } else {
      if (mode === 'range') {
        setPendingStartDate(null);
        setPendingEndDate(null);
        setSelectionMode('start');
      } else {
        setPendingDate(null);
      }
    }
  }, [isOpen, value, mode]);

  // ##### Format single date for display — delegates to shared formatConventions
  const formatSingleDate = (dateString) => formatDate(dateString);

  // ##### Format date for display — JSX for range (bold dates), string for single
  const formatDisplayDate = (value) => {
    if (mode === 'range') {
      if (!value || typeof value !== 'object') return placeholder;
      if (!value.start && !value.end) return placeholder;

      if (value.start && value.end) {
        if (value.start === value.end) {
          return <span><strong>{formatSingleDate(value.start)}</strong></span>;
        } else {
          return (
            <span>
              <strong>{formatSingleDate(value.start)}</strong> to <strong>{formatSingleDate(value.end)}</strong>
            </span>
          );
        }
      }
      if (value.start) return <span><strong>{formatSingleDate(value.start)}</strong></span>;
      return placeholder;
    } else {
      if (!value) return placeholder;
      const formatted = formatSingleDate(value);
      return formatted ? <span><strong>{formatted}</strong></span> : placeholder;
    }
  };

  // ##### Get calendar days for current month
  const getCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const firstDayWeek = firstDay.getDay();
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const prevMonth = new Date(year, month, 0);
    const daysInPrevMonth = prevMonth.getDate();

    const days = [];

    for (let i = firstDayWeek - 1; i >= 0; i--) {
      days.push({ date: new Date(year, month - 1, daysInPrevMonth - i), isCurrentMonth: false, isToday: false });
    }

    const today = new Date();
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      days.push({ date, isCurrentMonth: true, isToday: date.toDateString() === today.toDateString() });
    }

    const remainingDays = 42 - days.length;
    for (let day = 1; day <= remainingDays; day++) {
      days.push({ date: new Date(year, month + 1, day), isCurrentMonth: false, isToday: false });
    }

    return days;
  };

  // ##### Compare two date strings (YYYY-MM-DD)
  const compareDateStrings = (date1, date2) => {
    if (date1 < date2) return -1;
    if (date1 > date2) return 1;
    return 0;
  };

  // ##### Handle date selection (temporary — doesn't close dropdown)
  const handleDateSelect = (date, e) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    const dateString = formatDateLocal(date);

    if (mode === 'range') {
      const clickedIsFromValue = isFromValue(date, 'start') || isFromValue(date, 'end');

      if (clickedIsFromValue) {
        setPendingStartDate(dateString);
        setPendingEndDate(null);
        setSelectionMode('end');
        return;
      }

      if (pendingStartDate && pendingEndDate) {
        setPendingStartDate(dateString);
        setPendingEndDate(null);
        setSelectionMode('end');
        return;
      }

      if (!pendingStartDate) {
        setPendingStartDate(dateString);
        setPendingEndDate(null);
        setSelectionMode('end');
        return;
      }

      if (pendingStartDate && !pendingEndDate) {
        if (dateString === pendingStartDate) {
          setPendingEndDate(dateString);
        } else {
          if (compareDateStrings(dateString, pendingStartDate) < 0) {
            setPendingStartDate(dateString);
            setPendingEndDate(pendingStartDate);
          } else {
            setPendingEndDate(dateString);
          }
        }
        return;
      }
    } else {
      setPendingDate(dateString);
    }
  };

  // ##### Handle Today button
  const handleToday = (e) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    const today = new Date();
    setCurrentMonth(new Date(today.getFullYear(), today.getMonth(), 1));

    if (mode === 'range') {
      if (selectionMode === 'start') {
        setPendingStartDate(formatDateLocal(today));
        setSelectionMode('end');
      } else {
        setPendingEndDate(formatDateLocal(today));
      }
    } else {
      handleDateSelect(today, e);
    }
  };

  // ##### Handle Apply — save and close
  const handleApply = (e) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }

    if (mode === 'range') {
      if (pendingStartDate) {
        const endDate = pendingEndDate || pendingStartDate;
        if (onChange) {
          onChange({ target: { name: name, value: { start: pendingStartDate, end: endDate } } });
        }
      }
    } else {
      if (pendingDate && onChange) {
        onChange({ target: { name: name, value: pendingDate } });
      }
    }
    setIsOpen(false);
  };

  // ##### Navigate months
  const handlePrevMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  const handleNextMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));

  const getMonthYearLabel = () => currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  // ##### Portal position — auto-detects if there's space below, opens above if not
  const DROPDOWN_HEIGHT = 320; // conservative estimate for the calendar panel height

  const getPortalStyle = () => {
    if (!buttonRef.current) return {};
    const rect = buttonRef.current.getBoundingClientRect();

    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openAbove = spaceBelow < DROPDOWN_HEIGHT && spaceAbove > spaceBelow;

    // Clamp left so the dropdown doesn't overflow the right edge of the viewport
    const left = Math.min(rect.left, window.innerWidth - 288 - 8); // 288px = w-72

    return {
      position: 'fixed',
      top: openAbove ? `${rect.top - DROPDOWN_HEIGHT - 8}px` : `${rect.bottom + 8}px`,
      left: `${left}px`,
      zIndex: dropdownZIndex,
    };
  };

  // ##### Range highlight checks (blue/pending — in-progress selection only)
  const isDateInRange = (date) => {
    if (mode !== 'range') return false;
    if (!pendingStartDate || !pendingEndDate) return false;
    if (pendingStartDate === pendingEndDate) return false;

    const [sy, sm, sd] = pendingStartDate.split('-').map(Number);
    const [ey, em, ed] = pendingEndDate.split('-').map(Number);
    const startDate = new Date(sy, sm - 1, sd);
    const endDate = new Date(ey, em - 1, ed);
    const dateTime = date.getTime();

    return dateTime > startDate.getTime() && dateTime < endDate.getTime();
  };

  // ##### Green range check (saved value)
  const isInRangeFromValue = (date) => {
    if (mode !== 'range') return false;
    if (pendingStartDate || pendingEndDate) return false;
    if (!value?.start || !value?.end) return false;
    if (value.start === value.end) return false;

    const [sy, sm, sd] = value.start.split('-').map(Number);
    const [ey, em, ed] = value.end.split('-').map(Number);
    const startDate = new Date(sy, sm - 1, sd);
    const endDate = new Date(ey, em - 1, ed);
    const dateTime = date.getTime();

    return dateTime > startDate.getTime() && dateTime < endDate.getTime();
  };

  // ##### Check if date matches saved value (green) — hidden when any pending state exists
  const isFromValue = (date, type) => {
    if (mode === 'range') {
      if (pendingStartDate || pendingEndDate) return false;
      if (type === 'start') {
        if (!value?.start) return false;
        const [y, m, d] = value.start.split('-').map(Number);
        const saved = new Date(y, m - 1, d);
        return date.getFullYear() === saved.getFullYear() && date.getMonth() === saved.getMonth() && date.getDate() === saved.getDate();
      } else if (type === 'end') {
        if (!value?.end) return false;
        const [y, m, d] = value.end.split('-').map(Number);
        const saved = new Date(y, m - 1, d);
        return date.getFullYear() === saved.getFullYear() && date.getMonth() === saved.getMonth() && date.getDate() === saved.getDate();
      }
    } else {
      if (pendingDate) return false;
      if (!value) return false;
      const [y, m, d] = value.split('-').map(Number);
      const saved = new Date(y, m - 1, d);
      return date.getFullYear() === saved.getFullYear() && date.getMonth() === saved.getMonth() && date.getDate() === saved.getDate();
    }
    return false;
  };

  // ##### Check if date matches pending state (blue)
  const isFromPending = (date, type) => {
    if (mode === 'range') {
      if (type === 'start') {
        if (!pendingStartDate) return false;
        const [y, m, d] = pendingStartDate.split('-').map(Number);
        const pd = new Date(y, m - 1, d);
        return date.getFullYear() === pd.getFullYear() && date.getMonth() === pd.getMonth() && date.getDate() === pd.getDate();
      } else if (type === 'end') {
        if (!pendingEndDate) return false;
        const [y, m, d] = pendingEndDate.split('-').map(Number);
        const pd = new Date(y, m - 1, d);
        return date.getFullYear() === pd.getFullYear() && date.getMonth() === pd.getMonth() && date.getDate() === pd.getDate();
      }
    } else {
      if (!pendingDate) return false;
      const [y, m, d] = pendingDate.split('-').map(Number);
      const pd = new Date(y, m - 1, d);
      return date.getFullYear() === pd.getFullYear() && date.getMonth() === pd.getMonth() && date.getDate() === pd.getDate();
    }
    return false;
  };

  const isStartDate = (date) => {
    if (mode !== 'range' || !pendingStartDate) return false;
    const [y, m, d] = pendingStartDate.split('-').map(Number);
    const sd = new Date(y, m - 1, d);
    return date.getFullYear() === sd.getFullYear() && date.getMonth() === sd.getMonth() && date.getDate() === sd.getDate();
  };

  const isEndDate = (date) => {
    if (mode !== 'range' || !pendingEndDate) return false;
    const [y, m, d] = pendingEndDate.split('-').map(Number);
    const ed = new Date(y, m - 1, d);
    return date.getFullYear() === ed.getFullYear() && date.getMonth() === ed.getMonth() && date.getDate() === ed.getDate();
  };

  const isDateSelected = (date) => {
    if (mode === 'range') {
      return isDateInRange(date);
    } else {
      const dateToCheck = pendingDate || value;
      if (!dateToCheck) return false;
      const [y, m, d] = dateToCheck.split('-').map(Number);
      const sd = new Date(y, m - 1, d);
      return date.getFullYear() === sd.getFullYear() && date.getMonth() === sd.getMonth() && date.getDate() === sd.getDate();
    }
  };

  const days = getCalendarDays();
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={(e) => { e.stopPropagation(); if (!disabled) setIsOpen(!isOpen); }}
        disabled={disabled}
        className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg focus:outline-none focus:ring-2 ${colors.focusRing} disabled:bg-gray-100 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors ${className}`}
      >
        <CalendarIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
        <span className={`text-xs ${(mode === 'range' ? (value?.start || value?.end) : value) ? 'text-gray-800' : 'text-gray-400'}`}>
          {formatDisplayDate(value)}
        </span>
      </button>

      {/* Calendar Dropdown */}
      {isOpen && createPortal(
        <div
          data-qapp-portal="datepicker"
          style={getPortalStyle()}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div
            ref={dropdownRef}
            className="bg-white rounded-lg shadow-xl border border-gray-200 p-2.5 w-72"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {/* Month Navigation */}
            <div className="flex items-center justify-between mb-2">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); handlePrevMonth(); }}
                className="p-0.5 hover:bg-gray-100 rounded transition-colors"
              >
                <ChevronLeftIcon className="w-4 h-4 text-gray-600" />
              </button>
              <h3 className="text-xs font-semibold text-gray-800">{getMonthYearLabel()}</h3>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleNextMonth(); }}
                className="p-0.5 hover:bg-gray-100 rounded transition-colors"
              >
                <ChevronRightIcon className="w-4 h-4 text-gray-600" />
              </button>
            </div>

            {/* Week Days Header */}
            <div className="grid grid-cols-7 gap-0.5 mb-1.5">
              {weekDays.map((day) => (
                <div key={day} className="text-[10px] font-medium text-gray-500 text-center py-0.5">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Days */}
            <div className="grid grid-cols-7 gap-0.5">
              {days.map((dayObj, index) => {
                const { date, isCurrentMonth, isToday } = dayObj;
                const day = date.getDate();
                const selected = isDateSelected(date);
                const isStart = mode === 'range' && isStartDate(date);
                const isEnd = mode === 'range' && isEndDate(date);
                const isInRange = mode === 'range' && isDateInRange(date) && !isStart && !isEnd;
                const isInRangeFromValueGreen = mode === 'range' && isInRangeFromValue(date);

                const isStartFromValue = mode === 'range' && isFromValue(date, 'start');
                const isEndFromValue = mode === 'range' && isFromValue(date, 'end');
                const isSingleFromValue = mode === 'single' && isFromValue(date);

                const isStartFromPending = mode === 'range' && isFromPending(date, 'start');
                const isEndFromPending = mode === 'range' && isFromPending(date, 'end');
                const isSingleFromPending = mode === 'single' && isFromPending(date);

                // Green = saved/confirmed (semantic, always green regardless of colorScheme)
                // Scheme color = pending/in-progress selection
                let bgColor = '';
                if (isStartFromValue || isEndFromValue || isSingleFromValue) {
                  bgColor = 'bg-green-600 text-white';
                } else if (isInRangeFromValueGreen) {
                  bgColor = 'bg-green-100';
                } else if (isStartFromPending || isEndFromPending || isSingleFromPending) {
                  bgColor = colors.selectedBg;
                } else if (isStart || isEnd) {
                  bgColor = colors.selectedBg;
                } else if (isInRange) {
                  bgColor = colors.rangeBg;
                } else if (selected && mode === 'single') {
                  bgColor = colors.selectedBg;
                }

                return (
                  <button
                    key={index}
                    type="button"
                    onClick={(e) => handleDateSelect(date, e)}
                    className={`
                      h-8 w-8 rounded text-xs transition-colors relative
                      ${!isCurrentMonth ? 'text-gray-300' : 'text-gray-700'}
                      ${isToday ? `font-semibold ring-1 ${colors.todayRing}` : ''}
                      ${bgColor}
                      ${!bgColor && !isInRange ? 'hover:bg-gray-100' : ''}
                      ${!isCurrentMonth && !bgColor && !isInRange ? 'cursor-default' : 'cursor-pointer'}
                    `}
                  >
                    {day}
                  </button>
                );
              })}
            </div>

            {/* Today, Cancel and Apply Buttons */}
            {(() => {
              const hasSelection = mode === 'range' ? pendingStartDate !== null : pendingDate !== null;
              return (
                <div className="mt-2 flex gap-1.5">
                  <button
                    type="button"
                    onClick={handleToday}
                    className={`flex-1 px-2 py-1 text-xs ${colors.todayBtn} rounded transition-colors flex items-center justify-center gap-1`}
                  >
                    <CalendarIcon className="w-3 h-3" />
                    Today
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); e.preventDefault(); setIsOpen(false); }}
                    className="flex-1 px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-600 rounded transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleApply}
                    disabled={!hasSelection}
                    className={`flex-1 px-2 py-1 text-xs rounded transition-colors font-medium ${
                      hasSelection
                        ? `${colors.applyBtn} text-white`
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    Apply
                  </button>
                </div>
              );
            })()}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default DatePicker;
